import {
  Icon,
  Switch,
  Text,
} from "@salestrak/ui";
import {
  cellEditable,
  cellGrants,
  type CellState,
} from "@salestrak/permissions";
import type { PermissionMatrixRole } from "#app/lib/data";
import {
  MatrixCard,
  MatrixRow,
  MatrixLead,
  MatrixCell,
  RoleColumnHeader,
} from "#app/components/settings/permissions/matrix-chrome";

// Rows = the role being invited ("Invite Members"). Columns = the role
// doing the inviting. Each cell renders its DB cell state:
//   no_locked  → × (system-imposed off, e.g. hierarchy ceiling / guests)
//   yes_locked → ✓ (system-imposed on, e.g. admins always can)
//   yes / no   → editable Switch
export function InviteGrantMatrix({
  inviterRoles,
  invitableRoles,
  stateByCell,
  onToggle,
  onRenameRole,
  onDeleteRole,
}: {
  inviterRoles: readonly PermissionMatrixRole[];
  invitableRoles: readonly PermissionMatrixRole[];
  stateByCell: Map<string, CellState>;
  onToggle: (inviterRoleId: string, invitableRoleId: string, granted: boolean) => void;
  onRenameRole: (role: { id: string; roleName: string }) => void;
  onDeleteRole: (role: { id: string; roleName: string }) => void;
}) {
  return (
    <MatrixCard>
      {/* Header — one tonal step below the card (`muted` fill, no re-declared
          surface): re-naming `card` here would repaint the container's own
          fill and stack a second card shadow, and the hard hairline under it
          read as a heavy table seam. */}
      <MatrixRow background="muted" columns={inviterRoles.length}>
        <MatrixLead justify="center" paddingY="sm">
          <Text size="xs" variant="muted" weight="semibold">
            ACTIONS
          </Text>
        </MatrixLead>
        {inviterRoles.map((inviter, i) => (
          <MatrixCell key={`${inviter.id}-${i}`} first={i === 0} paddingY="sm">
            <RoleColumnHeader
              role={inviter}
              onRename={onRenameRole}
              onDelete={onDeleteRole}
            />
          </MatrixCell>
        ))}
      </MatrixRow>

      {/* Rows: one per invitable target role. No horizontal dividers
          between rows — only vertical dividers between columns. Cells
          stretch to the full row height (default align) so the column
          borders form continuous vertical lines with no gaps. */}
      {invitableRoles.map((target, ri) => (
        <MatrixRow key={`${target.id}-${ri}`} columns={inviterRoles.length}>
          <MatrixLead gap="2xs">
            <Text size="sm" weight="semibold">
              Invite {target.roleName}
            </Text>
            <Text size="xs" variant="muted">
              Allow granting new members the {target.roleName} role on invite.
            </Text>
          </MatrixLead>
          {inviterRoles.map((inviter, ci) => {
            const state = stateByCell.get(`${inviter.id}:${target.id}`) ?? "no";
            return (
              <MatrixCell key={`${inviter.id}-${ci}`} first={ci === 0}>
                {!cellEditable(state) ? (
                  cellGrants(state) ? (
                    <Icon name="check" size="sm" color="muted" />
                  ) : (
                    <Icon name="x" size="sm" color="muted" />
                  )
                ) : (
                  <Switch
                    aria-label={`Invite ${target.roleName}: ${inviter.roleName}`}
                    checked={cellGrants(state)}
                    onCheckedChange={(next) =>
                      onToggle(inviter.id, target.id, next)
                    }
                  />
                )}
              </MatrixCell>
            );
          })}
        </MatrixRow>
      ))}
    </MatrixCard>
  );
}
